import React, { useEffect, useState } from 'react'
import { cx } from '@monorepo/helper/cx'

import { ChevronDown } from './icons'
import { useTranslations } from 'next-intl'

type FilterItemProps = {
  label: string
  active: boolean
  onClick: () => void
}
const FilterItem = ({ label, active, onClick }: FilterItemProps) => {
  return (
    <div
      className="relative my-2 cursor-pointer px-4 text-red-500 hover:text-gray-700"
      onClick={onClick}
    >
      {label}
      <div
        className={cx(
          'absolute right-0 top-0 mr-4 mt-1 flex h-4 w-4 content-center justify-center border border-solid border-red-500',
          active && 'bg-yellow-500',
        )}
      >
        <div className="text-xs text-red-500" style={{ marginTop: -1 }}>
          {active ? '✓' : ''}
        </div>
      </div>
    </div>
  )
}

type Domain = {
  id?: string
  name?: string
  value?: string
}

type FilterAreaProps = {
  filters: Domain[]
  applyFiltering: (selectionMap: Map<string, boolean>) => void
  selectedFilters: Map<string, boolean>
  title: string
  isOpen: boolean
  setIsOpen: (newIsOpen: boolean) => void
}

const FilterArea = ({
  filters,
  applyFiltering,
  selectedFilters,
  title,
  isOpen,
  setIsOpen,
}: FilterAreaProps) => {
  const t = useTranslations('projects')

  const generateInitialSelectionMap = () => {
    const selectionMap = filters
      .sort((a, b) => (a.name! > b.name! ? 1 : -1))
      .reduce(function (map, filter) {
        filter.id && map.set(filter.id, true)
        return map
      }, new Map<string, boolean>())

    return selectionMap
  }

  const [selectionMap, setSelectionMap] = useState(selectedFilters)

  const onClickFilterButton = (value: any) => {
    const selected = !selectionMap.get(value)
    selectionMap.set(value, selected)

    // This is necessary in order to trigger the update
    setSelectionMap(new Map(selectionMap))
  }

  const onClickAllSelected = () => {
    setSelectionMap(new Map(generateInitialSelectionMap()))
  }

  const onClickNoneSelected = () => {
    setSelectionMap(new Map())
  }

  useEffect(() => {
    applyFiltering(selectionMap)
  }, [applyFiltering, selectionMap])

  useEffect(() => {
    setSelectionMap(selectedFilters)
  }, [selectedFilters])

  return (
    <div
      className={cx(
        'w-72 cursor-pointer border border-solid border-red-500 bg-white pb-0',
        !isOpen && 'hover:bg-red-100',
      )}
    >
      <div
        className="my-2 px-4 text-center text-red-500"
        onClick={() => {
          setIsOpen(!isOpen)
        }}
      >
        {title}
        <div className="flex justify-center text-red-500">
          <ChevronDown
            className={`icon-fill-red h-4 transition duration-150 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </div>
      </div>
      {isOpen ? (
        <>
          <div className="px-4 pb-2 text-center">
            <span
              className="cursor-pointer text-red-500 hover:text-gray-700"
              onClick={onClickAllSelected}
            >
              {t('displayAll')}
            </span>
            <span className="text-red-500"> | </span>
            <span
              className="cursor-pointer text-red-500 hover:text-gray-700"
              onClick={onClickNoneSelected}
            >
              {t('displayNone')}
            </span>
          </div>
          {filters.map((filter) => (
            <div key={filter.id}>
              <FilterItem
                label={filter.name || 'No label'}
                active={Boolean(filter.id && selectionMap.get(filter.id))}
                onClick={() => {
                  onClickFilterButton(filter.id)
                }}
              />
            </div>
          ))}
        </>
      ) : null}
    </div>
  )
}

export default FilterArea
